import type { Corridor } from "../types.ts";
import { DungeonModel } from "../model/dungeon.ts";

/** Type of selection: room, corridor, or none */
export type SelectionType = "room" | "corridor" | "none";

/** Represents the current selection state */
export interface Selection {
  type: SelectionType;
  id: string | null;
}

/**
 * SelectionManager handles the current selection state for rooms and corridors.
 * Supports multi-selection for rooms.
 */
export class SelectionManager {
  current: Selection;
  private selectedRooms: Set<string> = new Set();

  constructor() {
    this.current = { type: "none", id: null };
  }

  /**
   * Select a room by its ID (replaces current selection).
   * @param roomId - The ID of the room to select
   */
  selectRoom(roomId: string): void {
    this.selectedRooms.clear();
    this.selectedRooms.add(roomId);
    this.current = { type: "room", id: roomId };
  }

  /**
   * Toggle a room in the multi-selection.
   * If not selected, adds it. If already selected, removes it.
   * @param roomId - The ID of the room to toggle
   */
  toggleRoom(roomId: string): void {
    if (this.selectedRooms.has(roomId)) {
      this.selectedRooms.delete(roomId);
      // Update current to reflect remaining selection
      if (this.selectedRooms.size === 0) {
        this.current = { type: "none", id: null };
      } else {
        // Set current to first remaining room
        this.current = { type: "room", id: this.selectedRooms.values().next().value! };
      }
    } else {
      this.selectedRooms.add(roomId);
      this.current = { type: "room", id: roomId };
    }
  }

  /**
   * Add a room to the multi-selection without clearing existing selection.
   * @param roomId - The ID of the room to add
   */
  addRoom(roomId: string): void {
    this.selectedRooms.add(roomId);
    this.current = { type: "room", id: roomId };
  }

  /**
   * Select a corridor by its ID (clears room selection).
   * @param corridorId - The ID of the corridor to select
   */
  selectCorridor(corridorId: string): void {
    this.selectedRooms.clear();
    this.current = { type: "corridor", id: corridorId };
  }

  /**
   * Clear the current selection.
   */
  clear(): void {
    this.selectedRooms.clear();
    this.current = { type: "none", id: null };
  }

  /**
   * Check if a specific room is currently selected.
   * @param roomId - The room ID to check
   * @returns true if the room is selected
   */
  isRoomSelected(roomId: string): boolean {
    return this.selectedRooms.has(roomId);
  }

  /**
   * Check if a specific item is currently selected.
   * @param type - The type of selection to check
   * @param id - The ID to check
   * @returns true if the item is selected, false otherwise
   */
  isSelected(type: SelectionType, id: string): boolean {
    if (type === "room") {
      return this.selectedRooms.has(id);
    }
    return this.current.type === type && this.current.id === id;
  }

  /**
   * Get the currently selected room ID (primary selection).
   * @returns The selected room ID, or null if no room is selected
   */
  getSelectedRoom(): string | null {
    return this.current.type === "room" ? this.current.id : null;
  }

  /**
   * Get all selected room IDs.
   * @returns Set of selected room IDs
   */
  getSelectedRooms(): Set<string> {
    return new Set(this.selectedRooms);
  }

  /**
   * Get the number of selected rooms.
   */
  getSelectedRoomCount(): number {
    return this.selectedRooms.size;
  }

  /**
   * Check if multiple rooms are selected.
   */
  hasMultipleRoomsSelected(): boolean {
    return this.selectedRooms.size > 1;
  }

  /**
   * Get the currently selected corridor ID.
   * @returns The selected corridor ID, or null if no corridor is selected
   */
  getSelectedCorridor(): string | null {
    return this.current.type === "corridor" ? this.current.id : null;
  }
}

/**
 * Select whatever is at a hex position.
 * Checks rooms first, then corridors, and updates the selection manager accordingly.
 * @param hexKey - String key representing the hex (e.g., "0,0")
 * @param dungeon - The dungeon model to search
 * @param selectionManager - The selection manager to update
 * @param additive - If true, add to selection instead of replacing
 */
export function selectAtHex(
  hexKey: string,
  dungeon: DungeonModel,
  selectionManager: SelectionManager,
  additive: boolean = false
): void {
  // Check if there's a room at this hex
  const room = dungeon.getRoom(hexKey);
  if (room) {
    if (additive) {
      selectionManager.toggleRoom(room.id);
    } else {
      selectionManager.selectRoom(room.id);
    }
    return;
  }

  // Check if there's a corridor at this hex
  for (const corridor of dungeon.corridors.values()) {
    if (corridor.hexes.includes(hexKey)) {
      selectionManager.selectCorridor(corridor.id);
      return;
    }
  }

  // Nothing found at this hex, clear selection (only if not additive)
  if (!additive) {
    selectionManager.clear();
  }
}
